import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Bell, Mail, Clock, Globe, BookMarked, LogOut, Loader2, Heart, CreditCard } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import DonationModal from '../components/DonationModal';
import AdSense from '../components/AdSense';

function Settings() {
  const { user, isAuthenticated, logout, updatePreferences } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Form state
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [notificationTime, setNotificationTime] = useState('09:00');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailTime, setEmailTime] = useState('07:00');
  const [timezone, setTimezone] = useState('America/New_York');
  const [studyType, setStudyType] = useState('Devotional');
  const [bibleVersion, setBibleVersion] = useState('NIV');
  const [outputLanguage, setOutputLanguage] = useState('English');
  const [showDonationModal, setShowDonationModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Load user preferences
    if (user?.preferences) {
      const prefs = user.preferences;
      setNotificationEnabled(prefs.notification_enabled || false);
      setNotificationTime(prefs.notification_time || '09:00');
      setEmailEnabled(prefs.email_enabled || false);
      setEmailTime(prefs.email_time || '07:00');
      setTimezone(prefs.timezone || 'America/New_York');
      setStudyType(prefs.study_type || 'Devotional');
      setBibleVersion(prefs.bible_version || 'NIV');
      setOutputLanguage(prefs.output_language || 'English');
    }

    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, [user, isAuthenticated, navigate]);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast({
        title: "Not Supported",
        description: "Notifications are not supported in this browser.",
        variant: "destructive"
      });
      return false;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);

    if (permission === 'granted') {
      toast({
        title: "Notifications Enabled",
        description: "You'll receive daily Bible study reminders."
      });
      return true;
    } else {
      toast({
        title: "Permission Denied",
        description: "Please enable notifications in your browser settings.",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleNotificationToggle = async (checked) => {
    if (checked && notificationPermission !== 'granted') {
      const granted = await requestNotificationPermission();
      if (!granted) {
        return;
      }
    }
    setNotificationEnabled(checked);
  };

  const handleSave = async () => {
    setLoading(true);

    const preferences = {
      notification_enabled: notificationEnabled,
      notification_time: notificationTime,
      email_enabled: emailEnabled,
      email_time: emailTime,
      timezone: timezone,
      study_type: studyType,
      bible_version: bibleVersion,
      output_language: outputLanguage
    };

    const result = await updatePreferences(preferences);

    if (result.success) {
      toast({
        title: "Settings Saved",
        description: "Your preferences have been updated successfully."
      });
    } else {
      toast({
        title: "Save Failed",
        description: result.error,
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast({
      title: "Logged Out",
      description: "You've been successfully logged out."
    });
  };

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney'
  ];

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of ['00', '30']) {
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        options.push({ value: time, label: displayTime });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className="settings-page">
      <Toaster />
      <DonationModal 
        isOpen={showDonationModal} 
        onClose={() => setShowDonationModal(false)} 
      />
      
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
              <img 
                src="/its-written-logo.png" 
                alt="Its Written AI Logo" 
                style={{ height: '50px', width: 'auto' }}
              />
              <h1 className="logo-text">Its Written AI</h1>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="logout-button">
              <LogOut style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="settings-container">
        <div className="settings-header">
          <h1 className="settings-title">Settings</h1>
          <p className="settings-description">
            Customize your daily Bible study experience
          </p>
        </div>

        <div className="settings-grid">
          {/* Daily Notifications Card */}
          <Card className="settings-card">
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Bell className="card-icon" style={{ color: '#F0C96A' }} />
                <div>
                  <CardTitle>Daily Phone Reminder</CardTitle>
                  <CardDescription>
                    Receive a notification on your device
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="setting-row">
                <div className="setting-info">
                  <Label htmlFor="notification-toggle">Enable Reminders</Label>
                  <p className="setting-description">
                    Get a daily notification at your chosen time
                  </p>
                </div>
                <Switch
                  id="notification-toggle"
                  checked={notificationEnabled}
                  onCheckedChange={handleNotificationToggle}
                />
              </div>

              {notificationPermission === 'denied' && (
                <div className="notification-warning">
                  <p>⚠️ Notifications are blocked. Please enable them in your browser settings.</p>
                </div>
              )}

              {notificationEnabled && (
                <div className="setting-row" style={{ marginTop: '1rem' }}>
                  <Label htmlFor="notification-time">
                    <Clock style={{ width: '16px', height: '16px', display: 'inline', marginRight: '0.5rem' }} />
                    Reminder Time
                  </Label>
                  <Select value={notificationTime} onValueChange={setNotificationTime}>
                    <SelectTrigger id="notification-time" style={{ width: '150px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Email Card */}
          <Card className="settings-card">
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Mail className="card-icon" style={{ color: '#1F4B8F' }} />
                <div>
                  <CardTitle>Daily Email Devotional</CardTitle>
                  <CardDescription>
                    Receive a study in your inbox every day
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="setting-row">
                <div className="setting-info">
                  <Label htmlFor="email-toggle">Enable Daily Email</Label>
                  <p className="setting-description">
                    Get your personalized study via email
                  </p>
                </div>
                <Switch
                  id="email-toggle"
                  checked={emailEnabled}
                  onCheckedChange={setEmailEnabled}
                />
              </div>

              {emailEnabled && (
                <div className="setting-row" style={{ marginTop: '1rem' }}>
                  <Label htmlFor="email-time">
                    <Clock style={{ width: '16px', height: '16px', display: 'inline', marginRight: '0.5rem' }} />
                    Delivery Time
                  </Label>
                  <Select value={emailTime} onValueChange={setEmailTime}>
                    <SelectTrigger id="email-time" style={{ width: '150px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Study Preferences Card */}
          <Card className="settings-card">
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <BookMarked className="card-icon" style={{ color: '#1F4B8F' }} />
                <div>
                  <CardTitle>Study Preferences</CardTitle>
                  <CardDescription>
                    Customize your default study settings
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="preferences-grid">
                <div className="preference-item">
                  <Label>Study Type</Label>
                  <Select value={studyType} onValueChange={setStudyType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Devotional">Devotional</SelectItem>
                      <SelectItem value="Small Group Study">Small Group Study</SelectItem>
                      <SelectItem value="Sermon / Deep Study">Sermon / Deep Study</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="preference-item">
                  <Label>Bible Version</Label>
                  <Select value={bibleVersion} onValueChange={setBibleVersion}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KJV">KJV</SelectItem>
                      <SelectItem value="NIV">NIV</SelectItem>
                      <SelectItem value="ESV">ESV</SelectItem>
                      <SelectItem value="NKJV">NKJV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="preference-item">
                  <Label>
                    <Globe style={{ width: '16px', height: '16px', display: 'inline', marginRight: '0.5rem' }} />
                    Output Language
                  </Label>
                  <Select value={outputLanguage} onValueChange={setOutputLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Portuguese (Brazil)">Portuguese (Brazil)</SelectItem>
                      <SelectItem value="Spanish">Spanish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="preference-item">
                  <Label>Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map(tz => (
                        <SelectItem key={tz} value={tz}>
                          {tz.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info Card */}
          <Card className="settings-card">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="account-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{user?.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{user?.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Member Since:</span>
                  <span className="info-value">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Support the Mission Card */}
          <Card className="settings-card donation-card">
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Heart className="card-icon" style={{ color: '#F0C96A' }} />
                <div>
                  <CardTitle>Support the Mission</CardTitle>
                  <CardDescription>
                    Help us spread God's Word to believers worldwide
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="donation-message">
                Its Written AI is free for everyone. If this ministry blesses you, consider supporting 
                us so we can continue sending daily Bible studies around the world.
              </p>
              <div className="donation-buttons-settings">
                <Button
                  onClick={() => setShowDonationModal(true)}
                  className="donation-settings-button"
                >
                  <Heart style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
                  Make a Donation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="settings-actions">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="action-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="action-button save-button"
          >
            {loading ? (
              <>
                <Loader2 className="button-icon animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>

        {/* Ad Unit - Bottom of Settings */}
        <div style={{ marginTop: '2rem' }}>
          <AdSense 
            slot="3333333333"
            format="auto"
            responsive={true}
            className="settings-bottom-ad"
            label={false}
          />
        </div>
      </div>
    </div>
  );
}

export default Settings;
